import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { loadUser } from '../actions/registerUser';
import { buildSupportWhatsAppUrl, buildSupportMailtoUrl } from '../utils/support';
import './ActivationBanner.css';

const CODE_LENGTH = 9;

const errorMessage = (err, fallback) =>
  (err.response && err.response.data && err.response.data.msg) || fallback;

// 9 alphanumeric characters, grouped as XXX-XXX-XXX as the customer
// types -- strips anything that isn't alphanumeric and uppercases,
// same normalization the server applies on submit (see
// route/api/users.js's /activate/verify), so a pasted code with
// lowercase letters or its own dashes still formats correctly.
const formatCodeInput = (raw) => {
  const cleaned = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, CODE_LENGTH);
  return [cleaned.slice(0, 3), cleaned.slice(3, 6), cleaned.slice(6, 9)]
    .filter(Boolean)
    .join('-');
};

// Owner-only prompt to start the activation term, whose length is the
// account's own validityYears (5 for pre-2026-08-17 customers, 3 for
// new ones -- see models/User.js) -- embedded in
// Dashboard.js's own top bar (never PublicProfile.js: a stranger
// scanning the card has no way to act on this, see PublicProfile.js's
// own notActivated branch). Used to be its own full-width banner above
// the bar; Dashboard.js now recolors the bar itself instead and renders
// this inline in the middle of it -- this component only ever renders
// the trigger (text+button, or button alone on narrow viewports) and
// the modal, never its own background/bar. Renders nothing once
// user.activatedAt is set, including the moment verify succeeds
// (loadUser() below refreshes it, so the prompt and any open modal
// disappear together without a separate dismiss action).
const ActivationBanner = ({ user, loadUser }) => {
  const [showModal, setShowModal] = useState(false);
  // 'email' | 'sending' | 'requestFailed' | 'code' | 'success'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  // Once loadUser() (triggered by a successful verify below) refreshes
  // user.activatedAt, this must not unmount a modal that's still showing
  // its success screen -- only the banner itself hides immediately;
  // the still-open modal stays until its own "Done" click closes it.
  if (!user) return null;
  if (user.activatedAt && !showModal) return null;

  const openModal = () => {
    setShowModal(true);
    setStep('email');
    setEmail(user.email || '');
    setCode('');
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setStep('email');
    setCode('');
    setError('');
  };

  // The email here is display/confirmation only -- it fills the
  // persistent support link's WhatsApp message. The server never reads
  // it: POST /activate/request identifies the account from the auth
  // token alone, same as it always has (see that route's own comment).
  const requestCode = async () => {
    setError('');
    try {
      await axios.post('/api/users/activate/request');
      setCode('');
      setStep('code');
    } catch (err) {
      // Not-eligible-yet (403 { notEligible: true }, server-side check
      // unchanged -- see /activate/request's own comment) renders
      // exactly like a genuine send: same "we sent a code" screen, no
      // distinct message. The persistent support link below covers "I
      // didn't get it" for both causes without revealing which one this
      // was.
      if (err.response && err.response.status === 403 && err.response.data.notEligible) {
        setCode('');
        setStep('code');
        return;
      }
      setError(errorMessage(err, 'Something went wrong. Please try again.'));
      // A request that fails after the modal was already showing the code
      // form (i.e. a resend) stays on the code form -- only the very
      // first request (step still 'sending') falls back to the
      // dedicated failure screen, since there's no code form to return
      // to yet.
      setStep((current) => (current === 'sending' ? 'requestFailed' : current));
    }
  };

  const submitEmail = (e) => {
    e.preventDefault();
    setStep('sending');
    requestCode();
  };

  const resend = async () => {
    setResending(true);
    await requestCode();
    setResending(false);
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    try {
      await axios.post('/api/users/activate/verify', { code: code.replace(/-/g, '') });
      setStep('success');
      loadUser();
    } catch (err) {
      setError(errorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setVerifying(false);
    }
  };

  // Generic enough to fit either cause (blocked, or genuinely sent but
  // never arrived) -- the customer never sees which one applies, see
  // requestCode's own comment.
  const supportMessage = `Hi, this is my email: ${email}. I'm not receiving my activation code.`;

  return (
    <>
      {!user.activatedAt && (
        <div className="activation-bar-prompt">
          <span className="activation-bar-prompt__text">Your card isn't activated yet.</span>
          <button type="button" className="activation-bar-prompt__btn" onClick={openModal}>
            Activate now
          </button>
        </div>
      )}

      {/* Portaled to document.body -- position:fixed is normally
          viewport-relative regardless of DOM nesting, but an ancestor
          with its own transform/overflow context can trap it instead
          (see NavMenu.js's own comment on the identical problem its
          slide-in panel hit). Now that this renders on more than just
          Dashboard.js (see AppTopBar.js), it needs the same escape
          hatch. The trigger button above stays inline -- only the
          modal itself is portaled, since only the modal is
          position:fixed and viewport-relative regardless of where it's
          mounted. */}
      {showModal && ReactDOM.createPortal(
        <div className="activation-modal-backdrop" onClick={closeModal}>
          <div className="activation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="activation-modal__header">
              <h4 className="activation-modal__title">Activate your Card</h4>
              <span className="activation-modal__close" onClick={closeModal} aria-label="Close">
                &times;
              </span>
            </div>

            {step === 'email' && (
              <form onSubmit={submitEmail}>
                <p className="activation-modal__disclosure">
                  Confirm the email on your account to continue.
                </p>
                <input
                  type="email"
                  className="activation-input activation-input--email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                />
                <button
                  type="submit"
                  className="activation-submit"
                  disabled={!email.trim()}
                >
                  Continue
                </button>
              </form>
            )}

            {step === 'sending' && <p>Sending a confirmation code to {user.email}…</p>}

            {step === 'requestFailed' && (
              <div>
                <p className="activation-field__error">{error}</p>
                <button type="button" className="activation-submit" onClick={requestCode}>
                  Try again
                </button>
              </div>
            )}

            {step === 'code' && (
              <form onSubmit={verifyCode}>
                <p className="activation-modal__disclosure">
                  We sent a code to {user.email}. Enter it below to activate your card and
                  start your {user.validityYears}-year term.
                </p>
                {error && <p className="activation-field__error">{error}</p>}
                <input
                  type="text"
                  autoCapitalize="characters"
                  maxLength={CODE_LENGTH + 2}
                  className="activation-input"
                  placeholder="000-000-000"
                  value={code}
                  onChange={(e) => setCode(formatCodeInput(e.target.value))}
                  autoFocus
                />
                <button
                  type="submit"
                  className="activation-submit"
                  disabled={verifying || code.replace(/-/g, '').length !== CODE_LENGTH}
                >
                  {verifying ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  type="button"
                  className="activation-resend"
                  onClick={resend}
                  disabled={resending}
                >
                  {resending ? 'Sending…' : "Didn't get it? Resend code"}
                </button>
              </form>
            )}

            {step === 'success' && (
              <div className="activation-modal__success">
                <h4>You're activated!</h4>
                <p>
                  Your {user.validityYears}-year term has started. A confirmation email is on
                  its way with the details.
                </p>
                <button type="button" className="activation-submit" onClick={closeModal}>
                  Done
                </button>
              </div>
            )}

            {/* Persistent regardless of step -- visible before anything's
                been submitted, after a successful send, and after a
                not-eligible-yet block (same visible screen as a success,
                see requestCode's own comment) alike. Not shown once
                actually activated -- nothing left to not be receiving. */}
            {step !== 'success' && (
              <p className="activation-modal__support-link">
                If you are not receiving the email please{' '}
                <a
                  href={buildSupportWhatsAppUrl(supportMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact support
                </a>{' '}
                or{' '}
                <a href={buildSupportMailtoUrl(supportMessage)}>email us</a>
              </p>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const mapStateToProps = (state) => ({ user: state.registerUser.user });

export default connect(mapStateToProps, { loadUser })(ActivationBanner);
